<template>
    <div class="lesson-chat-insert">
        <div v-if="!compact" class="small text-muted mb-2">
            Add notes or files you forgot to send during the live lesson. They will appear in the lesson chat history as inserted messages.
        </div>
        <div v-if="errorMessage" class="alert alert-danger small py-1 px-2 mb-2">{{ errorMessage }}</div>
        <div v-if="successMessage" class="alert alert-success small py-1 px-2 mb-2">{{ successMessage }}</div>
        <textarea
            v-model="text"
            class="form-control form-control-sm mb-2"
            :rows="compact ? 2 : 3"
            placeholder="Type a missed note for the student..."
            :disabled="sending"
        ></textarea>
        <div class="d-flex flex-wrap align-items-center">
            <label class="btn btn-sm btn-outline-secondary mb-0 mr-2" :class="{ disabled: sending }">
                <span v-if="fileUploading">Uploading…</span>
                <span v-else>Attach file</span>
                <input
                    type="file"
                    class="d-none"
                    accept=".doc,.docx,.png,.jpg,.jpeg,.pdf,.gif,.webp"
                    :disabled="sending || fileUploading"
                    @change="onFileSelected"
                >
            </label>
            <span v-if="selectedFileName" class="small text-muted mr-2 text-truncate" style="max-width: 180px;">
                {{ selectedFileName }}
                <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="clearFile" :disabled="sending">×</button>
            </span>
            <b-button
                size="sm"
                variant="primary"
                class="ml-auto"
                :disabled="!canSend || sending"
                @click="send"
            >
                <b-spinner v-if="sending" small class="mr-1"></b-spinner>
                Insert message
            </b-button>
        </div>
        <div v-if="!compact" class="small text-secondary mt-1">
            Accepts .doc, .png, .jpg, .jpeg, .pdf (max 2MB on report card; up to 20MB via chat file API).
        </div>
    </div>
</template>

<script>
import {
    buildFileChatMessageHtml,
    buildTextChatMessageHtml,
    combineChatMessageParts,
    normalizeLessonFileUrl,
} from '../../utils/lessonChatMessageHtml';

const REPORT_CARD_MAX_BYTES = 2097152;

export default {
    name: 'LessonChatInsertComponent',
    props: {
        schedule_item_id: {
            type: [String, Number],
            required: true,
        },
        api_token: {
            type: String,
            required: true,
        },
        compact: {
            type: Boolean,
            default: false,
        },
        max_file_bytes: {
            type: Number,
            default: REPORT_CARD_MAX_BYTES,
        },
    },
    data() {
        return {
            text: '',
            selectedFile: null,
            selectedFileName: '',
            fileUploading: false,
            sending: false,
            errorMessage: null,
            successMessage: null,
        };
    },
    computed: {
        canSend() {
            return (this.text && this.text.trim()) || this.selectedFile;
        },
    },
    methods: {
        lessonChatApiUrl(path) {
            return `/api/${path}?api_token=${encodeURIComponent(this.api_token || '')}`;
        },
        clearMessages() {
            this.errorMessage = null;
            this.successMessage = null;
        },
        clearFile() {
            this.selectedFile = null;
            this.selectedFileName = '';
        },
        onFileSelected(event) {
            this.clearMessages();
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) {
                return;
            }
            const allowed = ['pdf', 'doc', 'docx', 'jpeg', 'jpg', 'png', 'gif', 'webp'];
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (!allowed.includes(ext)) {
                this.errorMessage = `Only formats are allowed: ${allowed.join(', ')}`;
                return;
            }
            if (file.size > this.max_file_bytes) {
                const mb = Math.round(this.max_file_bytes / 1048576);
                this.errorMessage = `File size exceeds ${mb}MB.`;
                return;
            }
            this.selectedFile = file;
            this.selectedFileName = file.name;
        },
        async uploadFile(file) {
            const form = new FormData();
            form.append('file', file);
            form.append('schedule_item_id', this.schedule_item_id);
            const res = await window.axios.post(this.lessonChatApiUrl('lesson-room/share-file'), form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return normalizeLessonFileUrl(res.data.url);
        },
        async send() {
            if (!this.canSend || this.sending) {
                return;
            }
            this.clearMessages();
            this.sending = true;
            try {
                let fileHtml = '';
                if (this.selectedFile) {
                    this.fileUploading = true;
                    const url = await this.uploadFile(this.selectedFile);
                    fileHtml = buildFileChatMessageHtml(url, this.selectedFile.name);
                    this.fileUploading = false;
                }
                const textHtml = this.text && this.text.trim() ? buildTextChatMessageHtml(this.text.trim()) : '';
                const message = combineChatMessageParts(textHtml, fileHtml);
                const res = await window.axios.post(this.lessonChatApiUrl('insertLessonChat'), {
                    method: 'POST',
                    channelid: this.schedule_item_id,
                    schedule_item_id: this.schedule_item_id,
                    message,
                });
                if (res.data && res.data.success) {
                    this.text = '';
                    this.clearFile();
                    this.successMessage = res.data.message || 'Message inserted.';
                    this.$emit('sent', res.data.item || null);
                    if (this.compact) {
                        setTimeout(() => { this.successMessage = null; }, 3000);
                    }
                } else {
                    this.errorMessage = (res.data && res.data.message) || 'Could not insert message.';
                }
            } catch (err) {
                this.fileUploading = false;
                const msg = err.response && err.response.data && err.response.data.message;
                this.errorMessage = msg || 'Could not insert message. Please try again.';
            } finally {
                this.sending = false;
            }
        },
    },
};
</script>

<style scoped>
.lesson-chat-insert .btn.disabled {
    pointer-events: none;
    opacity: 0.65;
}
</style>
